import React, { useState, useEffect } from 'react';
import { useMutation, useQuery, useLazyQuery, useApolloClient } from '@apollo/client';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  Heading,
  VStack,
  useToast,
  Spinner,
  Text,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { CREATE_MATCH, LIST_MATCH_FORMATS, QUERY_MATCHES, GET_MEMBERS_BY_LAST_INITIAL } from '../../Queries';
import { useUser } from '../../Context';
import { Footer, WithSubnavigation } from '../../Components';

export const CreateMatch = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { user } = useUser();
  const client = useApolloClient();

  // Check if we're copying from an existing match
  const copyFrom = location.state?.copyFrom;

  // Helper function to convert Unix timestamp to datetime-local format
  const timestampToDatetimeLocal = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp * 1000);
    // Format: YYYY-MM-DDTHH:MM
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const [formData, setFormData] = useState({
    match_name: copyFrom?.match_name || '',
    match_category: copyFrom?.match_category || 'Rifle',
    match_discipline: copyFrom?.match_discipline || '',
    match_date: timestampToDatetimeLocal(copyFrom?.match_date),
    match_format_id: copyFrom?.match_format_id || '',
    match_status: 'UPCOMING',
    match_location: copyFrom?.match_location || 'Golden Gun Club',
    match_director: copyFrom?.match_director || '',
    match_director_email: copyFrom?.match_director_email || '',
    registration_deadline: timestampToDatetimeLocal(copyFrom?.registration_deadline),
    max_participants: copyFrom?.max_participants?.toString() || '',
    match_description: copyFrom?.match_description || '',
  });

  const [matchDirectors, setMatchDirectors] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(true);

  // Query available formats
  const { data: formatsData, loading: formatsLoading } = useQuery(LIST_MATCH_FORMATS, {
    variables: { is_template: true },
  });

  // Fetch all members to find match directors
  useEffect(() => {
    const fetchAllMembers = async () => {
      const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
      const members = [];
      
      // Fetch members for each letter
      for (const letter of alphabet) {
        try {
          const { data } = await client.query({
            query: GET_MEMBERS_BY_LAST_INITIAL,
            variables: { initial: letter, size: 1000 },
          });
          
          if (data?.getMembersByLastInitial?.members) {
            members.push(...data.getMembersByLastInitial.members);
          }
        } catch (error) {
          console.error(`Error fetching members for ${letter}:`, error);
        }
      }
      
      // Filter for match directors and admins
      const directors = members.filter(member => {
        const groups = member.member_details?.member_groups || [];
        return groups.includes('match_directors') || groups.includes('admins');
      });
      
      // Sort by last name
      directors.sort((a, b) => {
        const lastA = a.member_details?.member_last_name || '';
        const lastB = b.member_details?.member_last_name || '';
        return lastA.localeCompare(lastB);
      });
      
      setMatchDirectors(directors);
      setLoadingMembers(false);
    };
    
    fetchAllMembers();
  }, [client]);

  const [createMatch, { loading: creating }] = useMutation(CREATE_MATCH, {
    onCompleted: (data) => {
      toast({
        title: 'Match created successfully',
        status: 'success',
        duration: 3000,
      });
      navigate('/matches/manage');
    },
    onError: (error) => {
      toast({
        title: 'Error creating match',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    },
    // Refetch all QueryMatches queries regardless of variables
    refetchQueries: ['QueryMatches'],
    awaitRefetchQueries: true,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // If changing match director, also update the email
    if (name === 'match_director') {
      const selectedDirector = matchDirectors.find(d => {
        const firstName = d.member_details?.member_first_name || '';
        const lastName = d.member_details?.member_last_name || '';
        const fullName = `${firstName} ${lastName}`.trim();
        return fullName === value;
      });
      
      setFormData((prev) => ({
        ...prev,
        match_director: value,
        match_director_email: selectedDirector?.member_email || '',
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Convert date to Unix timestamp
    const matchDate = new Date(formData.match_date).getTime() / 1000;
    const registrationDeadline = formData.registration_deadline
      ? new Date(formData.registration_deadline).getTime() / 1000
      : null;

    const variables = {
      match_details: {
        match_name: formData.match_name,
        match_category: formData.match_category,
        match_discipline: formData.match_discipline,
        match_date: matchDate,
        match_format_id: formData.match_format_id,
        match_status: formData.match_status,
        match_location: formData.match_location,
        match_director: formData.match_director,
        match_director_email: formData.match_director_email,
        registration_deadline: registrationDeadline,
        max_participants: formData.max_participants ? parseInt(formData.max_participants) : null,
        match_description: formData.match_description,
      },
    };

    createMatch({ variables });
  };

  if (formatsLoading || loadingMembers) {
    return (
      <Box bg="#F7F7F7" minH="100vh">
        <WithSubnavigation />
        <Box pt="84px" px={{ base: '42px', md: '84px' }} pb={8} textAlign="center">
          <Spinner size="lg" />
          <Text mt={4}>Loading...</Text>
        </Box>
        <Footer />
      </Box>
    );
  }

  return (
    <Box bg="#F7F7F7" minH="100vh">
      <WithSubnavigation />
      <Box pt="84px" px={{ base: '42px', md: '84px' }} pb={8}>
        <Box maxW="800px" mx="auto">
          <Heading size="lg" mb={6}>
            Create New Match
          </Heading>

          {copyFrom && (
            <Alert status="info" mb={6}>
              <AlertIcon />
              Creating a copy of an existing match. You can edit all fields before saving.
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel>Match Name</FormLabel>
                <Input
                  name="match_name"
                  value={formData.match_name}
                  onChange={handleChange}
                  placeholder="e.g., March Monthly Highpower"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Category</FormLabel>
                <Select name="match_category" value={formData.match_category} onChange={handleChange}>
                  <option value="Rifle">Rifle</option>
                  <option value="Pistol">Pistol</option>
                  <option value="Shotgun">Shotgun</option>
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Discipline</FormLabel>
                <Select
                  name="match_discipline"
                  value={formData.match_discipline}
                  onChange={handleChange}
                  placeholder="Select discipline"
                >
                  <option value="CMP_HIGHPOWER">CMP Highpower</option>
                  <option value="COWBOY_SILHOUETTE">Cowboy Silhouette</option>
                  <option value="BLACK_POWDER_SILHOUETTE">Black Powder Silhouette</option>
                  <option value="NRA_ACTION_PISTOL">NRA Action Pistol</option>
                  <option value="BOWLING_PIN_MATCH">Bowling Pin Match</option>
                  <option value="TRAP_LEAGUE">Trap League</option>
                  <option value="SKEET_LEAGUE">Skeet League</option>
                  <option value="FIVE_STAND_LEAGUE">5-Stand League</option>
                  <option value="TOURNAMENTS">Tournaments</option>
                  <option value="OTHER">Other</option>
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Match Date</FormLabel>
                <Input
                  type="datetime-local"
                  name="match_date"
                  value={formData.match_date}
                  onChange={handleChange}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Match Format (Optional)</FormLabel>
                <Select
                  name="match_format_id"
                  value={formData.match_format_id}
                  onChange={handleChange}
                  placeholder="Select a format (optional)"
                >
                  {formatsData?.listMatchFormats?.map((format) => (
                    <option key={format.format_id} value={format.format_id}>
                      {format.format_details.format_name} ({format.format_details.match_category})
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Match Director</FormLabel>
                <Select
                  name="match_director"
                  value={formData.match_director}
                  onChange={handleChange}
                  placeholder="Select match director"
                >
                  {matchDirectors.map((director) => {
                    const firstName = director.member_details?.member_first_name || '';
                    const lastName = director.member_details?.member_last_name || '';
                    const fullName = `${firstName} ${lastName}`.trim();
                    const email = director.member_email;
                    
                    return (
                      <option key={email} value={fullName}>
                        {fullName}
                      </option>
                    );
                  })}
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Location</FormLabel>
                <Input
                  name="match_location"
                  value={formData.match_location}
                  onChange={handleChange}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Registration Deadline</FormLabel>
                <Input
                  type="datetime-local"
                  name="registration_deadline"
                  value={formData.registration_deadline}
                  onChange={handleChange}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Max Participants</FormLabel>
                <Input
                  type="number"
                  name="max_participants"
                  value={formData.max_participants}
                  onChange={handleChange}
                  placeholder="Leave empty for unlimited"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Description</FormLabel>
                <Textarea
                  name="match_description"
                  value={formData.match_description}
                  onChange={handleChange}
                  rows={4}
                  placeholder="Match details, requirements, etc."
                />
              </FormControl>

              <Box pt={4}>
                <Button
                  type="submit"
                  colorScheme="blue"
                  isLoading={creating}
                  mr={3}
                >
                  Create Match
                </Button>
                <Button onClick={() => navigate('/matches/manage')}>
                  Cancel
                </Button>
              </Box>
            </VStack>
          </form>
        </Box>
      </Box>
      <Footer />
    </Box>
  );
};
